import { useState } from 'react';
import { useTaxAnalysis } from '../../hooks/useApi';
import { formatAmount } from '../../utils/numberFormat';
import { Currency, TaxProjectionPoint, TaxActionItem } from '../../types/api.types';
import styles from './TaxPage.module.scss';

type Tab = 'overview' | 'india' | 'us-canada' | 'checklist';

const TABS: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Tax Overview' },
    { id: 'india', label: 'India & DTAA' },
    { id: 'us-canada', label: 'US & Canada' },
    { id: 'checklist', label: 'Action Plan' },
];

const PRIORITY_COLORS: Record<string, string> = {
    HIGH: styles.priorityHigh,
    MEDIUM: styles.priorityMedium,
    LOW: styles.priorityLow,
};

function TaxLineChart({ data, baseCurrency }: { data: TaxProjectionPoint[]; baseCurrency: Currency }) {
    const W = 700;
    const H = 220;
    const pad = { top: 10, right: 20, bottom: 28, left: 10 };
    const cW = W - pad.left - pad.right;
    const cH = H - pad.top - pad.bottom;

    const maxVal = Math.max(...data.map((d) => d.india + d.us + d.can), 1);
    const xAt = (i: number) => pad.left + (i / Math.max(data.length - 1, 1)) * cW;
    const yAt = (v: number) => pad.top + cH - (v / maxVal) * cH;

    const polyline = (vals: number[]) => vals.map((v, i) => `${xAt(i)},${yAt(v)}`).join(' ');

    const tickIndices = data.reduce<number[]>((acc, d, i) => {
        if (i === 0 || i === data.length - 1 || d.age % 5 === 0) acc.push(i);
        return acc;
    }, []);

    const fmt = (v: number) => formatAmount(Math.round(v), baseCurrency).replace(/\.00$/, '');

    return (
        <svg
            width="100%"
            viewBox={`0 0 ${W} ${H}`}
            className={styles.lineChart}
            role="img"
            aria-label="Annual tax projection by country"
        >
            {/* grid lines */}
            {[0.25, 0.5, 0.75, 1].map((f) => (
                <line
                    key={f}
                    x1={pad.left}
                    x2={W - pad.right}
                    y1={yAt(maxVal * f)}
                    y2={yAt(maxVal * f)}
                    stroke="var(--color-outline-variant, #e0e0e0)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                />
            ))}

            {/* India */}
            <polyline
                points={polyline(data.map((d) => d.india))}
                fill="none"
                stroke="#FF9933"
                strokeWidth="2.5"
                strokeLinejoin="round"
            />
            {/* US */}
            <polyline
                points={polyline(data.map((d) => d.us))}
                fill="none"
                stroke="#002366"
                strokeWidth="2.5"
                strokeLinejoin="round"
            />
            {/* Canada */}
            <polyline
                points={polyline(data.map((d) => d.can))}
                fill="none"
                stroke="#D52B1E"
                strokeWidth="2.5"
                strokeLinejoin="round"
            />

            {/* x-axis age labels */}
            {tickIndices.map((i) => (
                <text key={i} x={xAt(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--color-outline, #888)">
                    {data[i].age}
                </text>
            ))}

            {/* y-axis max label */}
            <text x={pad.left + 2} y={pad.top + 10} fontSize="9" fill="var(--color-outline, #888)">
                {fmt(maxVal)}
            </text>
        </svg>
    );
}

const PRIORITY_ORDER: Record<TaxActionItem['priority'], number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };

function ActionList({ items }: { items: TaxActionItem[] }) {
    const sorted = [...items].sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);
    const high = items.filter((a) => a.priority === 'HIGH').length;
    const medium = items.filter((a) => a.priority === 'MEDIUM').length;
    const low = items.filter((a) => a.priority === 'LOW').length;
    return (
        <>
            <p className={styles.checklistIntro}>
                {high} high-priority · {medium} medium · {low} low
            </p>
            <div className={styles.actionList}>
                {sorted.map((item, i) => (
                    <div key={i} className={styles.actionCard}>
                        <div className={styles.actionCardHeader}>
                            <span className={`${styles.priorityBadge} ${PRIORITY_COLORS[item.priority]}`}>
                                {item.priority}
                            </span>
                            <span className={styles.actionCategory}>{item.category}</span>
                        </div>
                        <h4 className={styles.actionTitle}>{item.title}</h4>
                        <p className={styles.actionDesc}>{item.description}</p>
                    </div>
                ))}
            </div>
        </>
    );
}

export function TaxPage() {
    const [activeTab, setActiveTab] = useState<Tab>('overview');
    const { data: tax, loading, error } = useTaxAnalysis();

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.pageHeader}>
                    <h1 className={styles.title}>Tax Deep-Dive</h1>
                </div>
                <div className={styles.loadingState}>Analysing your tax position…</div>
            </div>
        );
    }

    if (error || !tax) {
        return (
            <div className={styles.page}>
                <div className={styles.pageHeader}>
                    <h1 className={styles.title}>Tax Deep-Dive</h1>
                </div>
                <div className={styles.errorState}>
                    {error || 'No data available. Add assets and cashflows to see your tax analysis.'}
                </div>
            </div>
        );
    }

    const fmt = (v: number) => formatAmount(Math.round(v), tax.baseCurrency);

    const maxTax = Math.max(...tax.taxByAssetClass.map((t) => t.annualTax), 1);

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Tax Deep-Dive</h1>
                <p className={styles.subtitle}>
                    Annual tax analysis across India, US, and Canada — with DTAA credits and compliance flags.
                </p>
            </div>

            <div className={styles.tabs}>
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Screen E: Tax Overview ──────────────────────────────────── */}
            {activeTab === 'overview' && (
                <div className={styles.tabContent}>
                    <div className={styles.metricsRow}>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>TOTAL ANNUAL TAX</span>
                            <span className={styles.metricValue}>{fmt(tax.currentYearTax.total)}</span>
                            <span className={styles.metricSub}>{tax.baseCurrency}/year on current balances</span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>EFFECTIVE TAX RATE</span>
                            <span
                                className={`${styles.metricValue} ${tax.effectiveTaxRate > 0.25 ? styles.valuePoor : tax.effectiveTaxRate > 0.15 ? styles.valueModerate : styles.valueGood}`}
                            >
                                {(tax.effectiveTaxRate * 100).toFixed(1)}%
                            </span>
                            <span className={styles.metricSub}>on investment income</span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>TAX BY COUNTRY</span>
                            <div className={styles.countryTaxRow}>
                                <span className={styles.indiaTag}>IN</span>
                                <span className={styles.countryTaxAmount}>{fmt(tax.currentYearTax.india)}</span>
                            </div>
                            <div className={styles.countryTaxRow}>
                                <span className={styles.usTag}>US</span>
                                <span className={styles.countryTaxAmount}>{fmt(tax.currentYearTax.us)}</span>
                            </div>
                            <div className={styles.countryTaxRow}>
                                <span className={styles.canTag}>CA</span>
                                <span className={styles.countryTaxAmount}>{fmt(tax.currentYearTax.can)}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>Projected Annual Tax by Country</h3>
                        <div className={styles.chartLegend}>
                            <span className={styles.legendIndia}>India</span>
                            <span className={styles.legendUS}>United States</span>
                            <span className={styles.legendCAN}>Canada</span>
                        </div>
                        <TaxLineChart data={tax.projectedAnnualTax} baseCurrency={tax.baseCurrency} />
                        <p className={styles.chartNote}>
                            X-axis: your age. Y-axis: annual tax in {tax.baseCurrency} on investment income
                            (pre-retirement). Tax-deferred accounts (401k, RRSP) shown at 0 until withdrawal.
                        </p>
                    </div>

                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>Tax Drag by Asset</h3>
                        {tax.taxByAssetClass.length === 0 ? (
                            <p className={styles.emptyState}>No taxable assets found. Add assets to see breakdown.</p>
                        ) : (
                            <div className={styles.assetTaxList}>
                                {tax.taxByAssetClass
                                    .sort((a, b) => b.annualTax - a.annualTax)
                                    .map((item, i) => (
                                        <div key={i} className={styles.assetTaxRow}>
                                            <div className={styles.assetTaxMeta}>
                                                <span className={styles.assetTaxLabel}>{item.label}</span>
                                                <span className={styles.assetTaxType}>{item.taxType}</span>
                                            </div>
                                            <div className={styles.assetTaxBarWrap}>
                                                <div
                                                    className={`${styles.assetTaxBar} ${
                                                        item.country === 'INDIA'
                                                            ? styles.barIndia
                                                            : item.country === 'US'
                                                              ? styles.barUS
                                                              : styles.barCAN
                                                    }`}
                                                    style={{ width: `${(item.annualTax / maxTax) * 100}%` }}
                                                />
                                            </div>
                                            <span className={styles.assetTaxAmount}>{fmt(item.annualTax)}</span>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Screen F: India & DTAA ──────────────────────────────────── */}
            {activeTab === 'india' && (
                <div className={styles.tabContent}>
                    {tax.nroTdsWarning && (
                        <div className={styles.warningBanner}>
                            <span className={styles.warningIcon}>⚠</span>
                            <div>
                                <strong>NRO TDS: 31.2% withheld at source</strong>
                                <p>
                                    Your NRO account interest is subject to 30% TDS + 4% cess. This is withheld by your
                                    Indian bank automatically. You can reclaim it as a foreign tax credit on your US or
                                    Canadian tax return.
                                </p>
                            </div>
                        </div>
                    )}

                    {tax.dtaaAlert && (
                        <div className={styles.infoBanner}>
                            <span className={styles.infoIcon}>ℹ</span>
                            <div>
                                <strong>DTAA foreign tax credit available</strong>
                                <p>
                                    India-US and India-Canada tax treaties allow you to claim{' '}
                                    <strong>{fmt(tax.indiaTaxBreakdown.dtaaCreditsAvailable)}</strong> of NRO TDS as a
                                    credit on your {tax.dtaaAlert ? 'US/Canadian' : ''} return. File Form 1116 (US) or
                                    T2209 (Canada) with your Indian bank's Form 16A.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>India Income Tax Breakdown</h3>
                        <div className={styles.tableWrap}>
                            <table className={styles.breakdownTable}>
                                <thead>
                                    <tr>
                                        <th>Source</th>
                                        <th>Annual Income</th>
                                        <th>Tax Rate</th>
                                        <th>Tax Due</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tax.indiaTaxBreakdown.nroInterestIncome > 0 && (
                                        <tr>
                                            <td>NRO Account Interest</td>
                                            <td>{fmt(tax.indiaTaxBreakdown.nroInterestIncome)}</td>
                                            <td>31.2% TDS</td>
                                            <td className={styles.taxDue}>{fmt(tax.indiaTaxBreakdown.nroTds)}</td>
                                        </tr>
                                    )}
                                    {tax.indiaTaxBreakdown.rentalIncome > 0 && (
                                        <tr>
                                            <td>Rental Income (India)</td>
                                            <td>{fmt(tax.indiaTaxBreakdown.rentalIncome)}</td>
                                            <td>30% TDS (gross)</td>
                                            <td className={styles.taxDue}>{fmt(tax.indiaTaxBreakdown.rentalTds)}</td>
                                        </tr>
                                    )}
                                    {tax.indiaTaxBreakdown.ltcgIncome > 0 && (
                                        <tr>
                                            <td>Equity / MF Capital Gains</td>
                                            <td>{fmt(tax.indiaTaxBreakdown.ltcgIncome)}</td>
                                            <td>12.5% LTCG (above ₹1.25L)</td>
                                            <td className={styles.taxDue}>{fmt(tax.indiaTaxBreakdown.ltcgTax)}</td>
                                        </tr>
                                    )}
                                    <tr className={styles.totalRow}>
                                        <td colSpan={2}>
                                            <strong>Total India Tax</strong>
                                        </td>
                                        <td />
                                        <td className={styles.taxDue}>
                                            <strong>{fmt(tax.indiaTaxBreakdown.totalIndiaTax)}</strong>
                                        </td>
                                    </tr>
                                    {tax.dtaaAlert && (
                                        <tr className={styles.creditRow}>
                                            <td colSpan={2}>DTAA Foreign Tax Credit (claimable)</td>
                                            <td />
                                            <td className={styles.creditAmount}>
                                                −{fmt(tax.indiaTaxBreakdown.dtaaCreditsAvailable)}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>India New Regime Tax Slabs (FY 2024-25)</h3>
                        <div className={styles.slabTable}>
                            {[
                                { range: 'Up to ₹3,00,000', rate: 'Nil' },
                                { range: '₹3,00,001 – ₹7,00,000', rate: '5%' },
                                { range: '₹7,00,001 – ₹10,00,000', rate: '10%' },
                                { range: '₹10,00,001 – ₹12,00,000', rate: '15%' },
                                { range: '₹12,00,001 – ₹15,00,000', rate: '20%' },
                                { range: 'Above ₹15,00,000', rate: '30%' },
                            ].map((slab) => (
                                <div key={slab.range} className={styles.slabRow}>
                                    <span className={styles.slabRange}>{slab.range}</span>
                                    <span className={styles.slabRate}>{slab.rate}</span>
                                </div>
                            ))}
                        </div>
                        <p className={styles.cardNote}>
                            + 4% Health & Education Cess on computed tax. Standard deduction of ₹75,000 for salaried
                            income. NRO interest not eligible for slab — taxed at flat 30%.
                        </p>
                    </div>
                </div>
            )}

            {/* ── Screen G: US & Canada ───────────────────────────────────── */}
            {activeTab === 'us-canada' && (
                <div className={styles.tabContent}>
                    {tax.pficTrapDetected && (
                        <div className={styles.dangerBanner}>
                            <span className={styles.warningIcon}>🚨</span>
                            <div>
                                <strong>PFIC Trap Detected</strong>
                                <p>
                                    You hold Indian mutual funds ({tax.pficAccountLabels.join(', ')}) and have US
                                    residency. These are classified as Passive Foreign Investment Companies under US
                                    law. The default excess-distribution regime applies interest charges on top of
                                    ordinary income rates. File Form 8621 and consider a QEF election or switch to
                                    US-listed India ETFs.
                                </p>
                                <div className={styles.bannerMetrics}>
                                    <div className={styles.bannerMetric}>
                                        <span className={styles.bannerMetricLabel}>Accounts Affected</span>
                                        <span className={styles.bannerMetricValue}>{tax.pficAccountLabels.length}</span>
                                    </div>
                                    <div className={styles.bannerMetric}>
                                        <span className={styles.bannerMetricLabel}>Tax Treatment</span>
                                        <span className={styles.bannerMetricValue}>Excess Distribution</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {tax.departureTaxRisk && (
                        <div className={styles.warningBanner}>
                            <span className={styles.warningIcon}>⚠</span>
                            <div>
                                <strong>Canadian Departure Tax Risk</strong>
                                <p>
                                    Your residency timeline shows a planned move out of Canada. CRA deems you to have
                                    disposed of most assets at fair market value on your departure date. RRSP, RRIF, and
                                    TFSA are exempt. Consult a cross-border tax advisor at least 12 months before your
                                    departure.
                                </p>
                                <div className={styles.bannerMetrics}>
                                    <div className={styles.bannerMetric}>
                                        <span className={styles.bannerMetricLabel}>Estimated Departure Tax</span>
                                        <span className={styles.bannerMetricValue}>
                                            C${Math.round(tax.departureTaxEstimateCAD).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className={styles.bannerMetric}>
                                        <span className={styles.bannerMetricLabel}>Exempt</span>
                                        <span className={styles.bannerMetricValue}>RRSP, RRIF, TFSA</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {tax.rothConversionOpportunity && (
                        <div className={styles.infoBanner}>
                            <span className={styles.infoIcon}>💡</span>
                            <div>
                                <strong>Roth Conversion Opportunity</strong>
                                <p>
                                    You hold pre-tax US retirement accounts. During any lower-income year (sabbatical,
                                    early retirement gap, or between jobs) you can convert to a Roth IRA up to the top
                                    of the 12% or 22% bracket. Future qualified withdrawals will be permanently
                                    tax-free, and you avoid higher RMDs at age 73.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className={styles.twoCol}>
                        <div className={styles.card}>
                            <h3 className={styles.cardTitle}>US Federal Tax Brackets (2024)</h3>
                            <div className={styles.slabTable}>
                                {[
                                    { range: '$0 – $11,600', rate: '10%' },
                                    { range: '$11,601 – $47,150', rate: '12%' },
                                    { range: '$47,151 – $100,525', rate: '22%' },
                                    { range: '$100,526 – $191,950', rate: '24%' },
                                    { range: '$191,951 – $243,725', rate: '32%' },
                                    { range: '$243,726 – $609,350', rate: '35%' },
                                    { range: 'Above $609,350', rate: '37%' },
                                ].map((b) => (
                                    <div key={b.range} className={styles.slabRow}>
                                        <span className={styles.slabRange}>{b.range}</span>
                                        <span className={styles.slabRate}>{b.rate}</span>
                                    </div>
                                ))}
                            </div>
                            <p className={styles.cardNote}>
                                Standard deduction $14,600 (2024 single filer). Long-term capital gains: 0% / 15% / 20%.
                            </p>
                        </div>

                        <div className={styles.card}>
                            <h3 className={styles.cardTitle}>Canada Federal + Provincial (2024)</h3>
                            <div className={styles.slabTable}>
                                {[
                                    { range: 'C$0 – C$55,867', rate: '15% + ~10.5%' },
                                    { range: 'C$55,868 – C$111,733', rate: '20.5% + ~10.5%' },
                                    { range: 'C$111,734 – C$154,906', rate: '26% + ~10.5%' },
                                    { range: 'C$154,907 – C$220,000', rate: '29% + ~10.5%' },
                                    { range: 'Above C$220,000', rate: '33% + ~10.5%' },
                                ].map((b) => (
                                    <div key={b.range} className={styles.slabRow}>
                                        <span className={styles.slabRange}>{b.range}</span>
                                        <span className={styles.slabRate}>{b.rate}</span>
                                    </div>
                                ))}
                            </div>
                            <p className={styles.cardNote}>
                                Basic personal amount C$15,705. Capital gains: 50% inclusion rate. Provincial rate is a
                                blended estimate — varies by province.
                            </p>
                        </div>
                    </div>

                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}>Account Tax Treatment Summary</h3>
                        <div className={styles.accountTaxGrid}>
                            {[
                                { account: 'NRE', india: 'Tax-Free', us: 'Taxable', can: 'Taxable' },
                                { account: 'NRO', india: '31.2% TDS', us: 'FTC credit', can: 'FTC credit' },
                                { account: 'PPF', india: 'EEE exempt', us: 'Taxable*', can: 'Taxable*' },
                                {
                                    account: 'Equity / MF (India)',
                                    india: 'LTCG 12.5%',
                                    us: 'PFIC rules',
                                    can: 'Ordinary income',
                                },
                                {
                                    account: '401k / Trad IRA',
                                    india: 'N/A',
                                    us: 'Ord. income on withdrawal',
                                    can: 'N/A',
                                },
                                { account: 'Roth IRA', india: 'N/A', us: 'Tax-Free', can: 'Taxable in CAN' },
                                {
                                    account: 'RRSP',
                                    india: 'N/A',
                                    us: 'DTAA election needed',
                                    can: 'Ord. income on withdrawal',
                                },
                                { account: 'TFSA', india: 'N/A', us: 'Taxable (no US treaty)', can: 'Tax-Free' },
                            ].map((row: { account: string; india: string; us: string; can: string }) => (
                                <div key={row.account} className={styles.accountTaxRow}>
                                    <span className={styles.accountName}>{row.account}</span>
                                    <span className={styles.countryCell}>
                                        <span className={styles.indiaTag}>IN</span>
                                        {row.india}
                                    </span>
                                    <span className={styles.countryCell}>
                                        <span className={styles.usTag}>US</span>
                                        {row.us}
                                    </span>
                                    <span className={styles.countryCell}>
                                        <span className={styles.canTag}>CA</span>
                                        {row.can}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <p className={styles.cardNote}>
                            * PPF not recognized as tax-advantaged by IRS/CRA — growth may be taxable annually. Consult
                            a cross-border specialist.
                        </p>
                    </div>
                </div>
            )}

            {/* ── Screen H: Action Plan ───────────────────────────────────── */}
            {activeTab === 'checklist' && (
                <div className={styles.tabContent}>
                    {tax.actionItems.length === 0 ? (
                        <div className={styles.card}>
                            <p className={styles.emptyState}>
                                No action items identified. Add assets, cashflows, and a residency timeline to generate
                                personalised recommendations.
                            </p>
                        </div>
                    ) : (
                        <ActionList items={tax.actionItems} />
                    )}
                </div>
            )}
        </div>
    );
}
